import React, { useEffect, useState, useMemo } from 'react';
import { Container, Heading, Box, Spinner, Text, Center, Button, HStack} from '@chakra-ui/react';
import PodcastRecommendationCard from './PodcastRecommendationCard';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api.ts';

interface Recommendation {
  episode_id: string;
  reason: string;
  episode_title: string;
  podcast_name: string;
  podcast_image: string;
  days_since_last_episode: number | null;
  published_date: string;
}

interface ForYouProps {
  shouldRedirectOnEmpty: boolean;
}

const ITEMS_PER_PAGE = 10;

const ForYou: React.FC<ForYouProps> = ({ shouldRedirectOnEmpty }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get page from URL or default to 1
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Update URL when page changes
  const handlePageChange = (newPage: number) => {
    setSearchParams({ page: newPage.toString() });
  };

  // Memoize the sorted recommendations to prevent unnecessary re-renders
  const sortedRecommendations = useMemo(() => {
    return [...recommendations].sort((a, b) => 
      (a.days_since_last_episode || 0) - (b.days_since_last_episode || 0)
    );
  }, [recommendations]);

  useEffect(() => {
    let isMounted = true;
    
    const fetchRecommendations = async () => {
      try {
        const userProfile = localStorage.getItem('user_profile');
        const profile = userProfile ? JSON.parse(userProfile) : null;
        
        if (!profile?.email) {
          throw new Error('User email not found. Please log in again.');
        }

        const response = await fetch(`${API_BASE_URL}/api/recommendations/for-you`, {
          headers: {
            'x-user-email': profile.email
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch recommendations');
        }
        
        const data = await response.json();
        console.log('Raw recommendations data:', data);
        
        // Only update state if the component is still mounted
        if (isMounted) {
          setRecommendations(data);
          // Only redirect if shouldRedirectOnEmpty is true and there are no recommendations
          if (data.length === 0 && shouldRedirectOnEmpty) {
            navigate('/discover');
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'An error occurred');
          // Only redirect on error if shouldRedirectOnEmpty is true
          if (shouldRedirectOnEmpty) {
            navigate('/discover');
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRecommendations();
    
    // Cleanup function to prevent memory leaks and state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [navigate, shouldRedirectOnEmpty]); // Add navigate to dependency array

  // Validate current page
  useEffect(() => {
    const totalPages = Math.ceil(sortedRecommendations.length / ITEMS_PER_PAGE);
    if (currentPage > totalPages && totalPages > 0) {
      handlePageChange(totalPages);
    } else if (currentPage < 1) {
      handlePageChange(1);
    }
  }, [currentPage, sortedRecommendations.length]);

  // Memoize pagination calculations
  const { totalPages, startIndex, endIndex } = useMemo(() => {
    return {
      totalPages: Math.ceil(sortedRecommendations.length / ITEMS_PER_PAGE),
      startIndex: (currentPage - 1) * ITEMS_PER_PAGE,
      endIndex: currentPage * ITEMS_PER_PAGE
    };
  }, [currentPage, sortedRecommendations.length]);

  if (loading) {
    return (
      <Center minH="60vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center minH="60vh">
        <Text color="red.500">{error}</Text>
      </Center>
    );
  }

  const paginatedRecommendations = sortedRecommendations.slice(startIndex, endIndex);

  return (
    <Container maxW={{ base: "95%", md: "90%", lg: "container.lg" }} py={{ base: 4, md: 8 }}>
      <Heading as="h1" mb={{ base: 4, md: 8 }} fontSize={{ base: "2xl", md: "3xl" }}>
        For You
      </Heading>
      
      {recommendations.length === 0 ? (
        <Text color="gray.600" whiteSpace="pre-line">
          <i><b>Geez, you are hard to please.</b></i>
<br></br>
<br></br>
Or maybe you just forgot to say what you like? Set up your preferences <a href="https://docs.google.com/spreadsheets/d/1pVT2HcZpReBHYtXtarfbvDn61Maz5EiCH7X_2vDWNkA/edit?gid=185863229#gid=185863229" target="_blank" rel="noopener noreferrer" style={{ color: '#3182CE', textDecoration: 'underline' }}>here</a>.
After you do that, it can take up to 24 hours for recommendations to show up.  
<br></br>
<br></br>

In the meantime, head on over to the <Link to="/discover" style={{ color: '#3182CE', textDecoration: 'underline' }}>Discover</Link> page to find something new.
        </Text>
      ) : (
        <>
          <Box>
            {paginatedRecommendations.map((recommendation) => (
              <PodcastRecommendationCard
                key={recommendation.episode_id}
                episodeId={recommendation.episode_id}
                reason={recommendation.reason || 'No reason provided'}
                podcastImage={recommendation.podcast_image || '/placeholder-podcast.jpg'}
                episodeTitle={recommendation.episode_title}
                podcastName={recommendation.podcast_name}
                daysAgo={recommendation.days_since_last_episode}
              />
            ))}
          </Box>

          {totalPages > 1 && (
            <HStack spacing={{ base: 2, md: 4 }} justify="center" mt={{ base: 6, md: 8 }} flexWrap="wrap">
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 1}
                size={{ base: "sm", md: "md" }}
              >
                Previous
              </Button>
              <Text fontSize={{ base: "sm", md: "md" }}>
                Page {currentPage} of {totalPages}
              </Text>
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage === totalPages}
                size={{ base: "sm", md: "md" }}
              >
                Next
              </Button>
            </HStack>
          )}
        </>
      )}
    </Container>
  );
};

export default ForYou; 